import { memo, useMemo, useState, type CSSProperties } from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { BookedEvents, Event } from "../../types/Types";
import {
  formatEventDateTimeLabel,
  getEventCardPhase,
  isWithin48HoursBeforeEvent,
  type EventCardPhase,
} from "../../utils/eventDateUtils";
import { isEventEarlyBirdConfigured } from "../../utils/eventEarlyBirdUtils";
import { formatCurrencyAmount } from "../../utils/formatCurrency";
import { ClampedTextWithTooltip } from "./ClampedTextWithTooltip";
import {
  EVENT_CARD_IMAGES,
  META_CLASS,
  META_STYLE,
  PHASE_CARD_CLASS,
  SLOT,
  type EventsListMode,
} from "./eventsListShared";
import { getEventInviteUrl, shareEventInvite } from "../../utils/eventShare";
import "../css/EventListCard.css";

export const EventListCard = memo(function EventListCard({
  event,
  listMode,
  isBooked,
  isFavorite,
  onToggleFavorite,
  onBook,
  onViewBooked,
  onViewAttendees,
  getBookedEvent,
}: {
  event: Event;
  listMode: EventsListMode;
  isBooked: boolean;
  isFavorite: boolean;
  onToggleFavorite: (event: Event) => void;
  onBook: (eventId: number) => void;
  onViewBooked: (event: BookedEvents) => void;
  onViewAttendees: (eventId: number) => void;
  getBookedEvent: (eventId: number) => BookedEvents | undefined;
}) {
  const { t, i18n } = useTranslation();
  const [sharing, setSharing] = useState(false);
  const dateLocale = i18n.resolvedLanguage || i18n.language;
  const phase: EventCardPhase =
    listMode === "attended" ? "expired" : getEventCardPhase(event.eventDate);
  const hasEarlyBird = isEventEarlyBirdConfigured(event);
  const isEarlyBirdActive =
    hasEarlyBird &&
    !!event.earlyBirdDateEnd &&
    new Date(event.earlyBirdDateEnd) > new Date();
  const showAttendees =
    isBooked ||
    listMode === "attended" ||
    isWithin48HoursBeforeEvent(event.eventDate);

  const dynamicFields = useMemo(
    () => ({
      name: event.name ?? "",
      description: event.description ?? "",
      location: event.location ?? "",
    }),
    [event.name, event.description, event.location],
  );
  const { data: translatedFields } = useTranslateDynamicFields(
    dynamicFields,
    dateLocale,
  );
  const tx = (key: keyof typeof dynamicFields, fallback: string) =>
    translatedFields?.[key]?.trim() || fallback;

  const phaseLabel =
    phase === "today"
      ? t("events.today")
      : phase === "upcoming"
        ? t("events.upcoming")
        : t("events.ended");
  const ribbonLabel = isBooked ? t("events.bookedBanner") : phaseLabel;
  const ribbonIcon = isBooked
    ? "bi-check-circle-fill"
    : phase === "today"
      ? "bi-calendar-event-fill"
      : phase === "upcoming"
        ? "bi-clock-fill"
        : "bi-x-circle-fill";
  const ribbonTone = isBooked
    ? "text-white"
    : phase === "today"
      ? "bg-success text-white"
      : phase === "upcoming"
        ? "bg-project-gradient text-white"
        : "bg-danger text-white";
  const ribbonStyle: CSSProperties = {
    width: 120,
    top: 18,
    right: -28,
    transform: "rotate(45deg)",
    padding: "0.2rem 0",
    fontSize: "0.65rem",
    letterSpacing: "0.03em",
    ...(isBooked ? { backgroundColor: "#6f42c1" } : {}),
  };
  const title = tx("name", event.name);
  const description = tx("description", event.description ?? "");
  const locationLabel = event.isOnline
    ? t("events.onlineEvent")
    : tx("location", event.location ?? "") || t("common.na");
  const bookedMeetingLink =
    isBooked && event.isOnline
      ? (
          getBookedEvent(event.id)?.meetingLink ?? event.meetingLink ?? ""
        ).trim()
      : "";
  const currency = event.currency ?? "gbp";
  const imageUrl =
    event.bannerImage || EVENT_CARD_IMAGES[event.id % EVENT_CARD_IMAGES.length];
  const dateLabel = formatEventDateTimeLabel(event.eventDate, dateLocale);

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const result = await shareEventInvite({
        title,
        url: getEventInviteUrl(event),
      });
      if (result === "copied") toast.success(t("events.linkCopied"));
      if (result === "failed") toast.error(t("events.shareFailed"));
    } finally {
      setSharing(false);
    }
  };

  return (
    <article className={`${PHASE_CARD_CLASS[phase]} h-100 d-flex flex-column`}>
      <div className="event-list-card__accent" aria-hidden />
      <div className="p-2 pb-0">
        <div className="position-relative rounded-3 overflow-hidden">
          <div className="ratio ratio-16x9">
            <img
              src={imageUrl}
              alt={title}
              className="object-fit-cover w-100 h-100"
            />
          </div>
          <span className="position-absolute top-0 start-0 m-2 badge bg-white text-dark border border-white border-2 px-2 py-1 fw-normal small">
            <i className="bi bi-calendar3 me-1" aria-hidden />
            {dateLabel}
          </span>
          <div
            className="position-absolute top-0 end-0 overflow-hidden"
            style={{ width: 88, height: 88, pointerEvents: "none" }}
            aria-label={ribbonLabel}
          >
            <span
              className={`position-absolute ${ribbonTone} small fw-semibold text-uppercase d-inline-flex align-items-center justify-content-center gap-1 shadow-sm`}
              style={ribbonStyle}
            >
              <i className={`bi ${ribbonIcon}`} aria-hidden />
              {ribbonLabel}
            </span>
          </div>
        </div>
      </div>

      <div className="card-body px-3 py-2 d-flex flex-column small flex-grow-1">
        <div className="d-flex align-items-start gap-2 mb-1 w-100 min-w-0">
          <div
            className="overflow-hidden pe-1"
            style={{ ...SLOT.title, flex: "1 1 0", minWidth: 0 }}
          >
            <ClampedTextWithTooltip
              id={`event-title-${event.id}`}
              text={title}
              className="fw-bold text-dark mb-0"
              lines={1}
            />
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            {phase !== "expired" ? (
              <button
                type="button"
                className="btn btn-link p-0 text-dark flex-shrink-0 lh-1 mt-1"
                style={{ width: 24, minWidth: 24 }}
                aria-label={t("events.share")}
                title={t("events.share")}
                disabled={sharing}
                onClick={() => void handleShare()}
              >
                <i
                  className="bi bi-share"
                  style={{ fontSize: "1.05rem" }}
                  aria-hidden
                />
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-link p-0 text-dark flex-shrink-0 lh-1 mt-1"
              style={{ width: 24, minWidth: 24 }}
              aria-label={t("events.favorite")}
              aria-pressed={isFavorite}
              onClick={() => onToggleFavorite(event)}
            >
              <i
                className={`bi ${isFavorite ? "bi-bookmark-fill" : "bi-bookmark"}`}
                style={{ fontSize: "1.15rem" }}
                aria-hidden
              />
            </button>
          </div>
        </div>
        {description ? (
          <div className="mb-1" style={SLOT.desc}>
            <ClampedTextWithTooltip
              id={`event-desc-${event.id}`}
              text={description}
              className={META_CLASS}
              style={META_STYLE}
            />
          </div>
        ) : null}

        <div className="mt-auto w-100 min-w-0">
          <div className="d-flex align-items-start justify-content-between gap-2 min-w-0 w-100">
            <div
              className="d-flex flex-column overflow-hidden"
              style={{ ...SLOT.location, flex: "1 1 0", minWidth: 0 }}
            >
              {!event.isOnline ? (
                <div className="d-flex align-items-center overflow-hidden">
                  <i
                    className="bi bi-geo-alt me-1 flex-shrink-0 text-muted"
                    aria-hidden
                  />
                  <ClampedTextWithTooltip
                    id={`event-location-${event.id}`}
                    text={locationLabel}
                    className={`${META_CLASS} min-w-0`}
                    style={META_STYLE}
                    lines={1}
                  />
                </div>
              ) : (
                <div className="d-flex align-items-center overflow-hidden">
                  <i
                    className="bi bi-camera-video me-1 flex-shrink-0 text-muted"
                    aria-hidden
                  />
                  <span className={`${META_CLASS} min-w-0`} style={META_STYLE}>
                    {locationLabel}
                  </span>
                </div>
              )}
              {bookedMeetingLink ? (
                <a
                  href={bookedMeetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="small text-primary text-truncate mt-1"
                  style={{ maxWidth: "100%" }}
                >
                  {t("events.meetingLink")}
                </a>
              ) : null}
            </div>
            <div
              className="text-end flex-shrink-0 d-flex flex-column justify-content-center ms-1"
              style={{ ...SLOT.pricing, maxWidth: "42%" }}
            >
              <p
                className="fw-bold text-primary mb-0 text-truncate lh-sm"
                style={META_STYLE}
              >
                {t("events.price")}:{" "}
                {formatCurrencyAmount(event.price, currency)}
              </p>
              {hasEarlyBird && (
                <>
                  <p
                    className={`text-muted mb-0 text-truncate lh-sm ${
                      !isEarlyBirdActive ? "text-decoration-line-through" : ""
                    }`}
                    style={{ fontSize: "11px" }}
                    title={`${t("events.earlyBirdDiscount")}: ${formatCurrencyAmount(event.earlyBirdPrice, currency)}`}
                  >
                    {t("events.earlyBirdDiscount")}:{" "}
                    {formatCurrencyAmount(event.earlyBirdPrice, currency)}
                  </p>
                  {isEarlyBirdActive && (
                    <p
                      className="mb-0 lh-sm text-truncate"
                      style={{ fontSize: "11px" }}
                      title={`${t("events.earlyBirdEndsOn")}: ${new Date(
                        event.earlyBirdDateEnd!,
                      ).toLocaleDateString(dateLocale)}`}
                    >
                      <span className="text-success">
                        {t("events.earlyBirdEndsOn")}:{" "}
                        {new Date(event.earlyBirdDateEnd!).toLocaleDateString(
                          dateLocale,
                        )}
                      </span>
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          <hr className="mt-2 mb-2" />

          <div className="d-flex align-items-stretch gap-2">
            {showAttendees && (
              <Button
                size="sm"
                variant="outline-primary"
                type="button"
                className="flex-grow-1 fw-semibold d-inline-flex align-items-center justify-content-center btn-hover-navy-fill"
                onClick={() => onViewAttendees(event.id)}
              >
                {t("events.viewAttendees")}
              </Button>
            )}
            {listMode === "attended" || isBooked ? (
              <Button
                size="sm"
                className="flex-grow-1 fw-semibold bg-project-gradient border-0 text-white"
                type="button"
                onClick={() => {
                  const booked = getBookedEvent(event.id);
                  if (booked) onViewBooked(booked);
                }}
              >
                {t("events.viewBookedEvent")}
              </Button>
            ) : event.seats > 0 ? (
              <Button
                variant="primary"
                size="sm"
                className="flex-grow-1 fw-semibold text-white"
                type="button"
                onClick={() => onBook(event.id)}
              >
                {t("events.bookNow")}
              </Button>
            ) : (
              <Button
                variant="danger"
                size="sm"
                className="flex-grow-1 fw-semibold text-white"
                disabled
                type="button"
              >
                {t("events.soldOut")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
});
